/**
 * Normaliza eventos de drag and drop a un formato consistente
 * independiente de la librería subyacente
 * 
 * Este módulo permite cambiar de librería de DnD sin afectar
 * la lógica de negocio que consume los eventos
 */

/**
 * Normaliza el evento onDragStart
 * 
 * @param {Object} event - Evento raw de la librería
 * @returns {Object} Evento normalizado { activeId, activeData }
 * 
 * @example
 * // @dnd-kit format
 * const rawEvent = { active: { id: '123', data: { current: { foo: 'bar' } } } };
 * const normalized = normalizeDragStartEvent(rawEvent);
 * // { activeId: '123', activeData: { foo: 'bar' } }
 */
export function normalizeDragStartEvent(event) {
  // Formato @dnd-kit
  if (event.active) {
    return {
      activeId: event.active.id,
      activeData: event.active.data?.current,
    };
  }
  
  // Formato react-beautiful-dnd (para migración futura)
  if (event.draggableId !== undefined) {
    return {
      activeId: event.draggableId,
      activeData: { index: event.source?.index },
    };
  }
  
  // Si no coincide con ningún formato conocido, devolver tal cual
  return event;
}

/**
 * Normaliza el evento onDragEnd
 * 
 * @param {Object} event - Evento raw de la librería
 * @returns {Object} Evento normalizado { activeId, overId, activeData, overData, _raw }
 * 
 * @example
 * // @dnd-kit format
 * const rawEvent = { 
 *   active: { id: '1', data: { current: { index: 0 } } },
 *   over: { id: '2', data: { current: { index: 1 } } }
 * };
 * const normalized = normalizeDragEndEvent(rawEvent);
 * // { activeId: '1', overId: '2', activeData: {...}, overData: {...}, _raw: {...} }
 */
export function normalizeDragEndEvent(event) {
  // Formato @dnd-kit
  if (event.active) {
    return {
      activeId: event.active.id,
      overId: event.over?.id || null,
      activeData: event.active.data?.current,
      overData: event.over?.data?.current,
      // Mantener el evento original por si acaso
      _raw: event,
    };
  }
  
  // Formato react-beautiful-dnd (para migración futura)
  if (event.draggableId !== undefined) {
    return {
      activeId: event.draggableId,
      overId: event.destination?.droppableId || null,
      activeData: { 
        droppableId: event.source?.droppableId,
        index: event.source?.index 
      },
      overData: { 
        droppableId: event.destination?.droppableId,
        index: event.destination?.index 
      },
      _raw: event,
    };
  }
  
  // Si no coincide con ningún formato conocido, devolver tal cual
  return event;
}

/**
 * Normaliza el evento onDragOver
 * 
 * @param {Object} event - Evento raw de la librería
 * @returns {Object} Evento normalizado
 */
export function normalizeDragOverEvent(event) {
  // Formato @dnd-kit
  if (event.active && event.over) {
    return {
      activeId: event.active.id,
      overId: event.over.id,
      activeData: event.active.data?.current,
      overData: event.over.data?.current,
      _raw: event,
    };
  }
  
  return event;
}

/**
 * Normaliza el objeto transform para transformaciones CSS
 * 
 * @param {Object|null} transform - Transform raw de la librería
 * @returns {Object|null} { x, y } o null
 * 
 * @example
 * // @dnd-kit format
 * const transform = { x: 10, y: 20 };
 * const normalized = normalizeTransform(transform);
 * // { x: 10, y: 20 }
 * 
 * // react-beautiful-dnd format
 * const transform = { translate: { x: 10, y: 20 } };
 * const normalized = normalizeTransform(transform);
 * // { x: 10, y: 20 }
 */
export function normalizeTransform(transform) {
  if (!transform) return null;
  
  // @dnd-kit format: { x, y }
  if (typeof transform.x === 'number' && typeof transform.y === 'number') {
    return { x: transform.x, y: transform.y };
  }
  
  // react-beautiful-dnd format: { translate: { x, y } }
  if (transform.translate) {
    return { 
      x: transform.translate.x || 0, 
      y: transform.translate.y || 0 
    };
  }
  
  // Si tiene formato de string (ej: "translate3d(10px, 20px, 0)")
  if (typeof transform === 'string') {
    const regex = /translate3d\((-?\d+)px,\s*(-?\d+)px/;
    const matches = regex.exec(transform);
    if (matches) {
      return {
        x: parseInt(matches[1], 10),
        y: parseInt(matches[2], 10),
      };
    }
  }
  
  return null;
}
